import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { FolderPlus, FileText, BarChart3, HardHat } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CreateAssessmentDialog from "../../CreateAssessmentDialog";
import CreateFolderDialog from "../../CreateFolderDialog";
import { CardActionItem } from "./CardActionItem";

interface ModernActionBarProps {
  projectId: string;
  folderId: string;
  isShared: boolean;
  onStartAssessment?: (projectId: string, folderId: string) => void;
  onCreateFolder: (name: string, description?: string) => Promise<void>;
  onRefresh?: () => void;
}

export const ModernActionBar: React.FC<ModernActionBarProps> = ({
  projectId,
  folderId,
  isShared,
  onStartAssessment,
  onCreateFolder,
  onRefresh
}) => {
  const navigate = useNavigate();
  const [isCreateAssessmentDialogOpen, setIsCreateAssessmentDialogOpen] = useState(false);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);
  const [folderName, setFolderName] = useState("");

  const handleCreateAssessmentSuccess = () => {
    setIsCreateAssessmentDialogOpen(false);
    onRefresh?.();
  };

  const handleCreateFolderSubmit = async () => {
    if (!folderName.trim()) return;
    await onCreateFolder(folderName.trim());
    setFolderName("");
    setIsCreateFolderDialogOpen(false);
    onRefresh?.();
  };

  const handleCreateSKRAssessment = () => {
    navigate(`/skr-bedomning/${projectId}/${folderId}?createNew=true`);
  };

  const handleCreateWorkPreparation = () => {
    navigate(`/arbetsberedning/${projectId}/${folderId}`);
  };

  return (
    <>
      <Card className="mb-6 sticky top-2 z-30 border border-border/50 hero-dark-gradient backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm animate-fade-in">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-lg font-semibold text-on-hero mb-1">
                Hantera innehåll
              </h2>
              <p className="text-sm text-on-hero/80">
                {onStartAssessment ? "Skapa nya mappar och risksammanställningar" : "Skapa en mapp först, skapa sedan din risksammanställning inne i mappen"}
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <CardActionItem
                icon={FolderPlus}
                title="Ny mapp"
                description="Skapa en ny mapp för att organisera ditt innehåll"
                onClick={() => setIsCreateFolderDialogOpen(true)}
              />
              
              {onStartAssessment && (
                <>
                  <CardActionItem
                    icon={FileText}
                    title="Ny risksammanställning"
                    description="Skapa en ny risksammanställning för projektet"
                    onClick={() => setIsCreateAssessmentDialogOpen(true)}
                    variant="primary"
                  />
                  
                  <CardActionItem
                    icon={BarChart3}
                    title="Ny SKR-bedömning"
                    description="Skapa en ny SKR-bedömning för systematisk riskanalys"
                    onClick={handleCreateSKRAssessment}
                  />
                  
                  <CardActionItem
                    icon={HardHat}
                    title="Ny arbetsberedning"
                    description="Skapa en ny arbetsberedning för säker arbetsutförande"
                    onClick={handleCreateWorkPreparation}
                  />
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {onStartAssessment && (
        <CreateAssessmentDialog
          isOpen={isCreateAssessmentDialogOpen}
          onOpenChange={setIsCreateAssessmentDialogOpen}
          projectId={projectId}
          folderId={folderId}
          onSuccess={handleCreateAssessmentSuccess}
          isSharedFolder={isShared}
        />
      )}

      <CreateFolderDialog
        isOpen={isCreateFolderDialogOpen}
        onOpenChange={setIsCreateFolderDialogOpen}
        onSubmit={handleCreateFolderSubmit}
        folderName={folderName}
        setFolderName={setFolderName}
      />
    </>
  );
};